import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { ORIENTATION_RIGHT_TO_LEFT } from '@Components/D3Tree/constants';
import RouteOverviewTreeRootItem from '@Pages/RetroSynthesis/RouteTree/components/renderers/RouteOverviewTreeRootItem';
import RouteOverviewTreeItem from '@Pages/RetroSynthesis/RouteTree/components/renderers/RouteOverviewTreeItem';
import RsTree from '@Components/D3Tree/Tree/RsTree';
import Button from '@Components/Button';
import RouteOverviewTruncationItem from '@Pages/RetroSynthesis/RouteTree/components/renderers/RouteOverviewTruncationItem';

import './RouteThumbnailsTree.theme.scss';

/* eslint-disable react-hooks/exhaustive-deps */
export const renderCustomNodeElement = ({ nodeDatum = {}, ...rest }, additionalProps = {}) => {
  const isRootNode = !nodeDatum.parentId;
  const isEndNode = isEmpty(nodeDatum.children);
  if (isRootNode) {
    return <RouteOverviewTreeRootItem nodeDatum={nodeDatum} {...rest} {...additionalProps} />;
  }
  if (nodeDatum.isTruncationNode) {
    return <RouteOverviewTruncationItem nodeDatum={nodeDatum} {...rest} {...additionalProps} />;
  }
  const nodeClassName = isEndNode ? 'node__leaf' : 'node__branch';
  return <RouteOverviewTreeItem classNames={nodeClassName} nodeDatum={nodeDatum} {...rest} {...additionalProps} />;
};

function RouteThumbnailsTree({
  tree,
  list,
  routeName,
  onTreeNodeClick,
  onZoomClick,
  allowTruncation,
  nodeWidth,
  nodeHeight,
  depthFactor,
  rectSize,
  circleSize,
  focusableNodes,
}) {
  const treeContainerRef = useRef();
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [treeSize, setTreeSize] = useState();
  const [isTruncated, setIsTruncated] = useState();

  const truncatedTree = useMemo(() => {
    const children = [{ id: 'truncationNode', parentId: '0', isTruncationNode: true, substance: '', leafNode: false, children: [] }];
    return { ...tree, id: '0', children };
  }, [tree]);

  const resizeHandler = () => {
    if (!treeContainerRef.current) {
      return;
    }
    try {
      const { width, height } = treeContainerRef.current.containerSize;
      const x = width - 2 * nodeWidth;
      const y = height / 2;
      if (treeSize && allowTruncation) {
        setIsTruncated(x - nodeWidth / 2 < get(treeSize, 'width') || height - 2 * nodeHeight < get(treeSize, 'height'));
      }
      setTranslate({ x, y });
    } catch (e) {
      Logger.error('RouteThumbnailsTree - container size error:', e);
    }
  };

  const onResizeLayout = ({ treeBoundaries }) => {
    const width = treeBoundaries.xMax - treeBoundaries.xMin;
    const height = treeBoundaries.yMax - treeBoundaries.yMin;
    setTreeSize({ width, height });
  };

  useLayoutEffect(() => {
    if (treeContainerRef) {
      window.addEventListener('resize', resizeHandler);
    }
    // TODO check ways of element size detection
    setTimeout(resizeHandler, 500);
    resizeHandler();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [treeSize, isTruncated, tree]);

  const renderNodeHandler = ({ nodeDatum = {}, ...rest }) => {
    return renderCustomNodeElement({ nodeDatum, ...rest }, { rectSize, circleSize, focusableNodes });
  };

  return (
    <div className="route-thumbnail-tree--container">
      {list && (
        <Button
          buttonConfig={{
            type: 'link',
            size: 'md',
            e2eName: 'zoom-in',
            label: 'Preview',
            noBorder: true,
          }}
          iconConfig={{ className: '', name: 'zoom', size: 'md' }}
          onClick={() => onZoomClick(list, routeName)}
        />
      )}
      <RsTree
        ref={treeContainerRef}
        onNodeClick={(id, event, data) => onTreeNodeClick(event.target, data)}
        nodeSize={{ x: nodeWidth, y: nodeHeight }}
        data={isTruncated ? truncatedTree : tree}
        separation={{
          siblings: 2,
          nonSiblings: 2,
        }}
        zoom={1}
        pathClassFunc={() => 'tree-wire'}
        zoomable={false}
        translate={translate}
        depthFactor={depthFactor}
        orientation={ORIENTATION_RIGHT_TO_LEFT}
        rootNodeClassName="node__root"
        branchNodeClassName="node__branch"
        leafNodeClassName="node__leaf"
        enableLegacyTransitions={false}
        collapsible={false}
        renderCustomNodeElement={renderNodeHandler}
        onResizeLayout={onResizeLayout}
      />
    </div>
  );
}

RouteThumbnailsTree.defaultProps = {
  onTreeNodeClick: () => null,
  onZoomClick: () => null,
  allowTruncation: true,
  nodeWidth: 10,
  nodeHeight: 10,
  depthFactor: 20,
  rectSize: 16,
  circleSize: 5,
  focusableNodes: true,
};

RouteThumbnailsTree.propTypes = {
  routeName: PropTypes.string,
  tree: PropTypes.any,
  list: PropTypes.any,
  onTreeNodeClick: PropTypes.func,
  onZoomClick: PropTypes.func,
  allowTruncation: PropTypes.bool,
  focusableNodes: PropTypes.bool,
  nodeWidth: PropTypes.number,
  nodeHeight: PropTypes.number,
  depthFactor: PropTypes.number,
  rectSize: PropTypes.number,
  circleSize: PropTypes.number,
};

export default RouteThumbnailsTree;
